export const PLATFORM_PLUGIN_API_VERSION = '1.0.0';
import { normalizeSettingsPluginPolicy } from './settings-plugin-policy.js';

export const APP_THEME_PACK_IDS = ['default', 'executive', 'compact', 'tutor-desk'];
export const APP_THEME_DEFAULT_PACK_ID = 'default';
export const APP_THEME_APPEARANCE_IDS = ['classic', 'figma'];
export const APP_THEME_DEFAULT_APPEARANCE_ID = 'classic';
export const APP_THEME_DEFAULT_APPEARANCE_PACK_ID = 'default';
export const APP_BASELINE_DEFAULTS = ['platform.settings', 'platform.auth-login'];
export const APP_RESOURCE_ISOLATION_VALUES = ['strict-app', 'legacy-shared'];
export const APP_RESOURCE_ISOLATION_DEFAULT = 'strict-app';
export const APP_DEPENDENCY_PROFILE_DEFAULT = 'platform-cloudflare-postgres-high-speed-v1';
export const APP_DEPENDENCY_PROFILE_APPROVED = [
  APP_DEPENDENCY_PROFILE_DEFAULT,
  'platform-cloudflare-postgres-core-v1',
  'platform-auth-core-v1',
  'platform-auth-core-v2',
  'platform-auth-core-v2-no-email',
  'platform-auth-core-v2-zero-n8n',
  'platform-auth-core-v2-zero-n8n-no-email'
];
export const APP_DEPENDENCY_EXTENSIONS_DEFAULT = Object.freeze([]);
export const APP_PREVIEW_MODE_VALUES = ['demo-local', 'real-local'];
export const APP_PREVIEW_MODE_DEFAULT = 'demo-local';
export const APP_PREVIEW_ADAPTER_DEFAULT = 'web-cloudflare-v1';
export const APP_PREVIEW_DATA_PROFILE_DEFAULT = 'default-demo-v1';
export const APP_SERVICE_TIER_VALUES = ['tier1', 'tier2', 'tier3'];
export const APP_SHELL_PRESET_VALUES = ['with-settings', 'empty', 'sectioned-routes'];
export const APP_SHELL_PRESET_DEFAULT = 'with-settings';
export const APP_AUTH_BASELINE_VALUES = ['with-auth-login', 'without-auth-login'];
export const APP_AUTH_BASELINE_DEFAULT = 'with-auth-login';
export const APP_SHELL_LAYOUT_VALUES = ['sidebar-standard', 'sidebar-routes'];
export const APP_SHELL_LAYOUT_DEFAULT = 'sidebar-standard';
export const APP_SHELL_NAV_MODE_VALUES = ['plugin-tabs', 'sectioned-routes'];
export const APP_SHELL_NAV_MODE_DEFAULT = 'plugin-tabs';
export const APP_SHELL_MOBILE_NAV_MODE_VALUES = ['stacked', 'drawer'];
export const APP_SHELL_MOBILE_NAV_MODE_DEFAULT = 'stacked';
export const PLATFORM_CORE_MODES = ['workspace', 'channel'];
export const PLATFORM_CORE_DEFAULT_MODE = 'workspace';
export const PLATFORM_CORE_FORBIDDEN_FLOATING_VERSIONS = ['latest', '*', 'main', 'canary', 'stable'];
export const PLATFORM_CORE_DEFAULT_MIN_VERSION = '0.0.0';
export const HIDDEN_NAV_PLUGIN_IDS = ['platform.auth-login'];
export const TESTING_ENGINE_BUSINESS_FALLBACK_POLICIES = ['off', 'only-uncovered', 'full'];
export const TESTING_ENGINE_V2_EXECUTION_MODES = ['legacy', 'parallel-v2', 'v2', 'v2-only'];
export const APP_DATA_PLANE_PROVIDERS = ['postgres', 'custom'];
export const APP_DATA_PLANE_BINDING_ROLES = ['cache-enabled', 'cache-disabled', 'read-write', 'fresh-read'];
export const APP_DATA_PLANE_CLIENTS = ['read-write', 'fresh-read', 'reporting', 'migration'];
export const APP_SURFACE_REVISION_SCOPES = ['workspace', 'surface', 'tenant', 'custom'];
export const APP_SURFACE_REVISION_SOURCES = ['server', 'read-model', 'mutation', 'custom'];
export const APP_READ_ROUTE_METHODS = ['GET'];
export const APP_READ_ROUTE_BINDING_ROLES = ['cache-enabled', 'cache-disabled', 'fresh-read'];
export const APP_MUTATION_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];
export const APP_REFRESH_SCOPES = ['surface', 'workspace', 'readModel', 'custom'];
export const APP_REFRESH_POLICIES = ['transactional', 'writeThenRepair', 'syncRequired'];
export const APP_PERFORMANCE_WORKLOAD_CATEGORIES = [
  'cold-load',
  'warm-navigation',
  'modal-open',
  'read',
  'write',
  'fresh-read-after-write',
  'critical-journey',
  'db-plan',
  'custom'
];
const DEFAULT_TESTING_ENGINE_BUDGETS = Object.freeze({
  maxUnitCasesPerBehavior: 3,
  maxIntegrationCasesPerBoundary: 3,
  maxWorkflowCasesPerWorkflow: 3,
  maxStressCasesPerApp: 25,
  fuzzNDefault: 10,
  fuzzNMax: 50,
  maxAiEnrichedCasesPerBehavior: 5,
  maxTotalGeneratedCasesPerApp: 2000,
  maxGeneratedFilesPerSuite: 2000,
  maxPlanOperationsPerSync: 5000,
  maxScenarioVariantsPerScenario: 4,
  maxStructuralFallbackCasesPerBehavior: 1,
  timeBudgetSeconds: Object.freeze({
    unit: 60,
    integration: 120,
    workflow: 120,
    stress: 180,
    verification: 120
  }),
  stressProfile: 'standard'
});

const DEFAULT_TESTING_ENGINE_GOLDEN = Object.freeze({
  enabled: true,
  mode: 'strict',
  required: true,
  maxGoldenFailuresToShow: 20
});

const DEFAULT_TESTING_ENGINE_BUSINESS = Object.freeze({
  enabled: false,
  structuralFallbackPolicy: 'only-uncovered',
  requireConcreteAssertions: true,
  minBusinessCoverageRatio: 0.8
});

const DEFAULT_TESTING_ENGINE_V2 = Object.freeze({
  executionMode: 'v2-only',
  primaryPilot: false,
  secondaryReference: false,
  enabledLanes: Object.freeze({
    model: true,
    shell: true,
    pluginAction: true,
    workflow: true,
    uiConsistency: true,
    stress: false
  }),
  sizePolicy: Object.freeze({
    presubmitBias: 'small-medium',
    largeLaneMaxPercent: 10
  }),
  hermeticPolicy: Object.freeze({
    enforceForSmall: true,
    enforceForMedium: true
  }),
  confidencePolicy: Object.freeze({
    requireCoverageClosure: true,
    requireConfidenceClosure: true
  }),
  quarantinePolicy: Object.freeze({
    ttlDays: 14,
    requireOwner: true
  }),
  ciPolicy: Object.freeze({
    requiredChecks: ['app-test-plan', 'app-test-runtime-critical', 'app-test-coverage-closure', 'app-test-confidence-closure']
  }),
  artifactPolicy: Object.freeze({
    traceMode: 'on-first-retry',
    screenshotMode: 'only-on-failure',
    videoMode: 'retain-on-failure'
  }),
  runtimeBacked: Object.freeze({
    shell: Object.freeze({
      enabled: false,
      maxCases: 6,
      maxWallClockSharePct: 15
    }),
    pluginAction: Object.freeze({
      enabled: false,
      criticalOnly: true,
      maxCases: 12,
      maxWallClockSharePct: 20
    }),
    workflow: Object.freeze({
      enabled: false,
      criticalOnly: true,
      maxCases: 8,
      maxWallClockSharePct: 20
    }),
    uiConsistency: Object.freeze({
      enabled: false,
      maxCases: 12,
      maxWallClockSharePct: 25
    })
  }),
  migrationPolicy: Object.freeze({
    primaryPromotionGate: false,
    defaultForNewApps: true,
    promotionWindowDays: 14,
    sunsetWindow: '1-release-cycle'
  })
});

const STRICT_BASELINES_BY_SHELL_PRESET = {
  'with-settings': ['platform.settings'],
  empty: [],
  'sectioned-routes': []
};

const APP_THEME_PACKS_BY_APPEARANCE = {
  classic: ['default'],
  figma: ['default', 'executive', 'compact', 'tutor-desk']
};

function asVersionMajor(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  const [major] = text.split('.');
  return major;
}

function parseSemverParts(value) {
  const text = String(value || '').trim();
  if (!text) return null;
  const match = text.match(/^(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?(?:\+([0-9A-Za-z.-]+))?$/);
  if (!match) return null;
  return {
    major: Number(match[1]),
    minor: Number(match[2]),
    patch: Number(match[3]),
    prerelease: match[4] || '',
    build: match[5] || ''
  };
}

function comparePrerelease(left = '', right = '') {
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  const leftParts = left.split('.');
  const rightParts = right.split('.');
  const length = Math.max(leftParts.length, rightParts.length);
  for (let idx = 0; idx < length; idx += 1) {
    const a = leftParts[idx];
    const b = rightParts[idx];
    if (a === undefined) return -1;
    if (b === undefined) return 1;
    const aNum = /^\d+$/.test(a) ? Number(a) : NaN;
    const bNum = /^\d+$/.test(b) ? Number(b) : NaN;
    const aIsNum = Number.isFinite(aNum);
    const bIsNum = Number.isFinite(bNum);
    if (aIsNum && bIsNum) {
      if (aNum !== bNum) return aNum > bNum ? 1 : -1;
      continue;
    }
    if (aIsNum !== bIsNum) {
      return aIsNum ? -1 : 1;
    }
    if (a !== b) {
      return a > b ? 1 : -1;
    }
  }
  return 0;
}

export function isSemverLike(value) {
  return Boolean(parseSemverParts(value));
}

export function compareSemver(leftValue, rightValue) {
  const left = parseSemverParts(leftValue);
  const right = parseSemverParts(rightValue);
  if (!left || !right) return NaN;
  if (left.major !== right.major) return left.major > right.major ? 1 : -1;
  if (left.minor !== right.minor) return left.minor > right.minor ? 1 : -1;
  if (left.patch !== right.patch) return left.patch > right.patch ? 1 : -1;
  return comparePrerelease(left.prerelease, right.prerelease);
}

export function isPluginApiCompatible(apiVersion) {
  return asVersionMajor(apiVersion) === asVersionMajor(PLATFORM_PLUGIN_API_VERSION);
}

function normalizePluginIdList(value) {
  if (!Array.isArray(value)) return [];
  const out = [];
  const seen = new Set();
  value.forEach((item) => {
    const pluginId = String(item || '').trim();
    if (!pluginId || seen.has(pluginId)) return;
    seen.add(pluginId);
    out.push(pluginId);
  });
  return out;
}

function normalizeStringList(value) {
  if (!Array.isArray(value)) return [];
  const out = [];
  const seen = new Set();
  value.forEach((item) => {
    const text = String(item || '').trim();
    if (!text || seen.has(text)) return;
    seen.add(text);
    out.push(text);
  });
  return out;
}

function isPlainObject(value) {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function normalizeObjectList(value, normalizeItem) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => isPlainObject(item))
    .map((item) => normalizeItem(item));
}

function normalizeOptionalObject(value, normalizeItem) {
  return isPlainObject(value) ? normalizeItem(value) : undefined;
}

function normalizeBudgetNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function normalizeDataPlane(value = {}) {
  const freshness = isPlainObject(value.freshness) ? value.freshness : {};
  return {
    version: String(value.version || '').trim(),
    provider: String(value.provider || '').trim().toLowerCase(),
    bindings: normalizeObjectList(value.bindings, (binding) => ({
      binding: String(binding.binding || '').trim(),
      role: String(binding.role || '').trim(),
      client: String(binding.client || '').trim(),
      sourceHeader: String(binding.sourceHeader || '').trim()
    })),
    freshness: {
      readSourceHeader: String(freshness.readSourceHeader || '').trim(),
      requiredRevisionHeader: String(freshness.requiredRevisionHeader || '').trim(),
      actualRevisionHeader: String(freshness.actualRevisionHeader || '').trim(),
      mutationRevisionHeader: String(freshness.mutationRevisionHeader || '').trim()
    }
  };
}

function normalizeManifestSurfaces(value) {
  return normalizeObjectList(value, (surface) => ({
    surfaceId: String(surface.surfaceId || '').trim(),
    title: String(surface.title || '').trim(),
    ownerPluginId: String(surface.ownerPluginId || '').trim(),
    routePath: String(surface.routePath || '').trim(),
    revisionScope: String(surface.revisionScope || '').trim(),
    revisionSource: String(surface.revisionSource || '').trim(),
    requiredRevisionHeader: String(surface.requiredRevisionHeader || '').trim(),
    readRouteRefs: normalizeStringList(surface.readRouteRefs),
    readModelRefs: normalizeStringList(surface.readModelRefs)
  }));
}

function normalizeManifestReadRoutes(value) {
  return normalizeObjectList(value, (route) => ({
    routeId: String(route.routeId || '').trim(),
    path: String(route.path || '').trim(),
    method: String(route.method || '').trim().toUpperCase(),
    surfaceRefs: normalizeStringList(route.surfaceRefs),
    bindingRole: String(route.bindingRole || '').trim(),
    sourceHeader: String(route.sourceHeader || '').trim(),
    supportsFreshRead: route.supportsFreshRead === true,
    performanceWorkloadRefs: normalizeStringList(route.performanceWorkloadRefs)
  }));
}

function normalizeResponseDelta(value = {}) {
  return {
    kind: String(value.kind || '').trim(),
    entity: String(value.entity || '').trim(),
    fields: normalizeStringList(value.fields)
  };
}

function normalizeManifestMutationSpecs(value) {
  return normalizeObjectList(value, (mutation) => ({
    mutationId: String(mutation.mutationId || '').trim(),
    route: String(mutation.route || '').trim(),
    method: String(mutation.method || '').trim().toUpperCase(),
    affectedSurfaces: normalizeStringList(mutation.affectedSurfaces),
    beforeKeys: normalizeStringList(mutation.beforeKeys),
    afterKeys: normalizeStringList(mutation.afterKeys),
    refreshScope: String(mutation.refreshScope || '').trim(),
    responseDelta: normalizeOptionalObject(mutation.responseDelta, normalizeResponseDelta),
    readModelRefs: normalizeStringList(mutation.readModelRefs),
    refreshPolicy: String(mutation.refreshPolicy || '').trim()
  }));
}

function normalizeManifestReadModels(value) {
  return normalizeObjectList(value, (model) => ({
    modelId: String(model.modelId || '').trim(),
    title: String(model.title || '').trim(),
    surfaceRefs: normalizeStringList(model.surfaceRefs),
    sourceTables: normalizeStringList(model.sourceTables),
    refreshPolicy: String(model.refreshPolicy || '').trim(),
    refreshCommand: String(model.refreshCommand || '').trim(),
    checkCommand: String(model.checkCommand || '').trim(),
    rebuildCommand: String(model.rebuildCommand || '').trim(),
    stalenessBudgetSeconds: normalizeBudgetNumber(model.stalenessBudgetSeconds)
  }));
}

function normalizePerformanceBudgets(value = {}) {
  const dbPlan = isPlainObject(value.dbPlan) ? value.dbPlan : {};
  return {
    coldLoadMs: normalizeBudgetNumber(value.coldLoadMs),
    warmNavigationMs: normalizeBudgetNumber(value.warmNavigationMs),
    modalOpenMs: normalizeBudgetNumber(value.modalOpenMs),
    readMs: normalizeBudgetNumber(value.readMs),
    writeMs: normalizeBudgetNumber(value.writeMs),
    freshReadAfterWriteMs: normalizeBudgetNumber(value.freshReadAfterWriteMs),
    payloadBytes: normalizeBudgetNumber(value.payloadBytes),
    criticalGetCount: normalizeBudgetNumber(value.criticalGetCount),
    longTaskCount: normalizeBudgetNumber(value.longTaskCount),
    transactionMs: normalizeBudgetNumber(value.transactionMs),
    regressionPct: normalizeBudgetNumber(value.regressionPct),
    dbPlan: {
      allowedClassifications: normalizeStringList(dbPlan.allowedClassifications)
    }
  };
}

function normalizeManifestPerformance(value = {}) {
  return {
    workloads: normalizeObjectList(value.workloads, (workload) => ({
      workloadId: String(workload.workloadId || '').trim(),
      category: String(workload.category || '').trim(),
      description: String(workload.description || '').trim(),
      surfaceRefs: normalizeStringList(workload.surfaceRefs),
      routeRefs: normalizeStringList(workload.routeRefs),
      mutationRefs: normalizeStringList(workload.mutationRefs),
      budgets: isPlainObject(workload.budgets) ? normalizePerformanceBudgets(workload.budgets) : undefined
    }))
  };
}

export function normalizePreviewModeList(value) {
  const normalized = normalizeStringList(value)
    .map((item) => String(item || '').trim().toLowerCase())
    .filter((item) => APP_PREVIEW_MODE_VALUES.includes(item));
  return normalized.length ? normalized : [APP_PREVIEW_MODE_DEFAULT];
}

function normalizeHelpFlowList(value, {
  allowPluginRefs = false
} = {}) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
    .map((item) => ({
      flowId: String(item.flowId || '').trim(),
      title: String(item.title || '').trim(),
      summary: String(item.summary || '').trim(),
      pluginRefs: allowPluginRefs ? normalizePluginIdList(item.pluginRefs) : [],
      capabilityRefs: normalizeStringList(item.capabilityRefs),
      featureFlagRefs: normalizeStringList(item.featureFlagRefs),
      docRefs: normalizeStringList(item.docRefs)
    }));
}

function normalizeAppHelp(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const normalized = {
    lifecycle: String(value.lifecycle || '').trim(),
    purpose: String(value.purpose || '').trim(),
    audience: normalizeStringList(value.audience),
    valueSummary: String(value.valueSummary || '').trim(),
    majorFlows: normalizeHelpFlowList(value.majorFlows, { allowPluginRefs: true }),
    docRefs: normalizeStringList(value.docRefs)
  };
  if (
    !normalized.lifecycle
    && !normalized.purpose
    && normalized.audience.length === 0
    && !normalized.valueSummary
    && normalized.majorFlows.length === 0
    && normalized.docRefs.length === 0
  ) {
    return undefined;
  }
  return normalized;
}

function normalizePluginHelp(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const normalized = {
    lifecycle: String(value.lifecycle || '').trim(),
    purpose: String(value.purpose || '').trim(),
    audience: normalizeStringList(value.audience),
    featureSummary: String(value.featureSummary || '').trim(),
    userFlows: normalizeHelpFlowList(value.userFlows),
    docRefs: normalizeStringList(value.docRefs)
  };
  if (
    !normalized.lifecycle
    && !normalized.purpose
    && normalized.audience.length === 0
    && !normalized.featureSummary
    && normalized.userFlows.length === 0
    && normalized.docRefs.length === 0
  ) {
    return undefined;
  }
  return normalized;
}

function normalizeFiniteNumber(value, fallback = 0, { min = 0, max = Number.MAX_SAFE_INTEGER } = {}) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

function normalizeTestingEngineBudgets(value = {}) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    maxUnitCasesPerBehavior: Math.floor(normalizeFiniteNumber(
      source.maxUnitCasesPerBehavior,
      DEFAULT_TESTING_ENGINE_BUDGETS.maxUnitCasesPerBehavior,
      { min: 1, max: 20 }
    )),
    maxIntegrationCasesPerBoundary: Math.floor(normalizeFiniteNumber(
      source.maxIntegrationCasesPerBoundary,
      DEFAULT_TESTING_ENGINE_BUDGETS.maxIntegrationCasesPerBoundary,
      { min: 1, max: 20 }
    )),
    maxWorkflowCasesPerWorkflow: Math.floor(normalizeFiniteNumber(
      source.maxWorkflowCasesPerWorkflow,
      DEFAULT_TESTING_ENGINE_BUDGETS.maxWorkflowCasesPerWorkflow,
      { min: 1, max: 20 }
    )),
    maxStressCasesPerApp: Math.floor(normalizeFiniteNumber(
      source.maxStressCasesPerApp,
      DEFAULT_TESTING_ENGINE_BUDGETS.maxStressCasesPerApp,
      { min: 1, max: 500 }
    )),
    fuzzNDefault: Math.floor(normalizeFiniteNumber(
      source.fuzzNDefault,
      DEFAULT_TESTING_ENGINE_BUDGETS.fuzzNDefault,
      { min: 1, max: 500 }
    )),
    fuzzNMax: Math.floor(normalizeFiniteNumber(
      source.fuzzNMax,
      DEFAULT_TESTING_ENGINE_BUDGETS.fuzzNMax,
      { min: 1, max: 500 }
    )),
    maxAiEnrichedCasesPerBehavior: Math.floor(normalizeFiniteNumber(
      source.maxAiEnrichedCasesPerBehavior,
      DEFAULT_TESTING_ENGINE_BUDGETS.maxAiEnrichedCasesPerBehavior,
      { min: 0, max: 50 }
    )),
    maxTotalGeneratedCasesPerApp: Math.floor(normalizeFiniteNumber(
      source.maxTotalGeneratedCasesPerApp,
      DEFAULT_TESTING_ENGINE_BUDGETS.maxTotalGeneratedCasesPerApp,
      { min: 1, max: 50000 }
    )),
    maxGeneratedFilesPerSuite: Math.floor(normalizeFiniteNumber(
      source.maxGeneratedFilesPerSuite,
      DEFAULT_TESTING_ENGINE_BUDGETS.maxGeneratedFilesPerSuite,
      { min: 1, max: 50000 }
    )),
    maxPlanOperationsPerSync: Math.floor(normalizeFiniteNumber(
      source.maxPlanOperationsPerSync,
      DEFAULT_TESTING_ENGINE_BUDGETS.maxPlanOperationsPerSync,
      { min: 1, max: 50000 }
    )),
    maxScenarioVariantsPerScenario: Math.floor(normalizeFiniteNumber(
      source.maxScenarioVariantsPerScenario,
      DEFAULT_TESTING_ENGINE_BUDGETS.maxScenarioVariantsPerScenario,
      { min: 1, max: 100 }
    )),
    maxStructuralFallbackCasesPerBehavior: Math.floor(normalizeFiniteNumber(
      source.maxStructuralFallbackCasesPerBehavior,
      DEFAULT_TESTING_ENGINE_BUDGETS.maxStructuralFallbackCasesPerBehavior,
      { min: 0, max: 20 }
    )),
    timeBudgetSeconds: {
      unit: Math.floor(normalizeFiniteNumber(
        source?.timeBudgetSeconds?.unit,
        DEFAULT_TESTING_ENGINE_BUDGETS.timeBudgetSeconds.unit,
        { min: 1, max: 7200 }
      )),
      integration: Math.floor(normalizeFiniteNumber(
        source?.timeBudgetSeconds?.integration,
        DEFAULT_TESTING_ENGINE_BUDGETS.timeBudgetSeconds.integration,
        { min: 1, max: 7200 }
      )),
      workflow: Math.floor(normalizeFiniteNumber(
        source?.timeBudgetSeconds?.workflow,
        DEFAULT_TESTING_ENGINE_BUDGETS.timeBudgetSeconds.workflow,
        { min: 1, max: 7200 }
      )),
      stress: Math.floor(normalizeFiniteNumber(
        source?.timeBudgetSeconds?.stress,
        DEFAULT_TESTING_ENGINE_BUDGETS.timeBudgetSeconds.stress,
        { min: 1, max: 7200 }
      )),
      verification: Math.floor(normalizeFiniteNumber(
        source?.timeBudgetSeconds?.verification,
        DEFAULT_TESTING_ENGINE_BUDGETS.timeBudgetSeconds.verification,
        { min: 1, max: 7200 }
      ))
    },
    stressProfile: ['light', 'standard', 'heavy'].includes(String(source.stressProfile || '').trim().toLowerCase())
      ? String(source.stressProfile || '').trim().toLowerCase()
      : DEFAULT_TESTING_ENGINE_BUDGETS.stressProfile
  };
}

function normalizeTestingEngineGolden(value = {}) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    enabled: source.enabled !== false,
    mode: String(source.mode || DEFAULT_TESTING_ENGINE_GOLDEN.mode).trim().toLowerCase() === 'warn'
      ? 'warn'
      : 'strict',
    required: source.required !== false,
    maxGoldenFailuresToShow: Math.floor(normalizeFiniteNumber(
      source.maxGoldenFailuresToShow,
      DEFAULT_TESTING_ENGINE_GOLDEN.maxGoldenFailuresToShow,
      { min: 1, max: 500 }
    ))
  };
}

function normalizeTestingEngineBusiness(value = {}) {
  const source = value && typeof value === 'object' ? value : {};
  const policy = String(source.structuralFallbackPolicy || DEFAULT_TESTING_ENGINE_BUSINESS.structuralFallbackPolicy)
    .trim()
    .toLowerCase();
  return {
    enabled: source.enabled === true,
    structuralFallbackPolicy: TESTING_ENGINE_BUSINESS_FALLBACK_POLICIES.includes(policy)
      ? policy
      : DEFAULT_TESTING_ENGINE_BUSINESS.structuralFallbackPolicy,
    requireConcreteAssertions: source.requireConcreteAssertions !== false,
    minBusinessCoverageRatio: normalizeFiniteNumber(
      source.minBusinessCoverageRatio,
      DEFAULT_TESTING_ENGINE_BUSINESS.minBusinessCoverageRatio,
      { min: 0, max: 1 }
    )
  };
}

function normalizeTestingEngineV2(value = {}) {
  const source = value && typeof value === 'object' ? value : {};
  const executionMode = String(source.executionMode || DEFAULT_TESTING_ENGINE_V2.executionMode).trim().toLowerCase();
  const enabledLanes = source.enabledLanes && typeof source.enabledLanes === 'object' && !Array.isArray(source.enabledLanes)
    ? source.enabledLanes
    : {};
  const sizePolicy = source.sizePolicy && typeof source.sizePolicy === 'object' && !Array.isArray(source.sizePolicy)
    ? source.sizePolicy
    : {};
  const hermeticPolicy = source.hermeticPolicy && typeof source.hermeticPolicy === 'object' && !Array.isArray(source.hermeticPolicy)
    ? source.hermeticPolicy
    : {};
  const confidencePolicy = source.confidencePolicy && typeof source.confidencePolicy === 'object' && !Array.isArray(source.confidencePolicy)
    ? source.confidencePolicy
    : {};
  const quarantinePolicy = source.quarantinePolicy && typeof source.quarantinePolicy === 'object' && !Array.isArray(source.quarantinePolicy)
    ? source.quarantinePolicy
    : {};
  const ciPolicy = source.ciPolicy && typeof source.ciPolicy === 'object' && !Array.isArray(source.ciPolicy)
    ? source.ciPolicy
    : {};
  const artifactPolicy = source.artifactPolicy && typeof source.artifactPolicy === 'object' && !Array.isArray(source.artifactPolicy)
    ? source.artifactPolicy
    : {};
  const runtimeBacked = source.runtimeBacked && typeof source.runtimeBacked === 'object' && !Array.isArray(source.runtimeBacked)
    ? source.runtimeBacked
    : {};
  const migrationPolicy = source.migrationPolicy && typeof source.migrationPolicy === 'object' && !Array.isArray(source.migrationPolicy)
    ? source.migrationPolicy
    : {};
  return {
    executionMode: TESTING_ENGINE_V2_EXECUTION_MODES.includes(executionMode)
      ? executionMode
      : DEFAULT_TESTING_ENGINE_V2.executionMode,
    primaryPilot: source.primaryPilot === true,
    secondaryReference: source.secondaryReference === true,
    enabledLanes: {
      model: enabledLanes.model !== false,
      shell: enabledLanes.shell !== false,
      pluginAction: enabledLanes.pluginAction !== false,
      workflow: enabledLanes.workflow !== false,
      uiConsistency: enabledLanes.uiConsistency !== false,
      stress: enabledLanes.stress === true
    },
    sizePolicy: {
      presubmitBias: String(sizePolicy.presubmitBias || DEFAULT_TESTING_ENGINE_V2.sizePolicy.presubmitBias).trim() || DEFAULT_TESTING_ENGINE_V2.sizePolicy.presubmitBias,
      largeLaneMaxPercent: Math.floor(normalizeFiniteNumber(
        sizePolicy.largeLaneMaxPercent,
        DEFAULT_TESTING_ENGINE_V2.sizePolicy.largeLaneMaxPercent,
        { min: 1, max: 100 }
      ))
    },
    hermeticPolicy: {
      enforceForSmall: hermeticPolicy.enforceForSmall !== false,
      enforceForMedium: hermeticPolicy.enforceForMedium !== false
    },
    confidencePolicy: {
      requireCoverageClosure: confidencePolicy.requireCoverageClosure !== false,
      requireConfidenceClosure: confidencePolicy.requireConfidenceClosure !== false
    },
    quarantinePolicy: {
      ttlDays: Math.floor(normalizeFiniteNumber(
        quarantinePolicy.ttlDays,
        DEFAULT_TESTING_ENGINE_V2.quarantinePolicy.ttlDays,
        { min: 1, max: 365 }
      )),
      requireOwner: quarantinePolicy.requireOwner !== false
    },
    ciPolicy: {
      requiredChecks: normalizeStringList(ciPolicy.requiredChecks).length > 0
        ? normalizeStringList(ciPolicy.requiredChecks)
        : [...DEFAULT_TESTING_ENGINE_V2.ciPolicy.requiredChecks]
    },
    artifactPolicy: {
      traceMode: String(artifactPolicy.traceMode || DEFAULT_TESTING_ENGINE_V2.artifactPolicy.traceMode).trim() || DEFAULT_TESTING_ENGINE_V2.artifactPolicy.traceMode,
      screenshotMode: String(artifactPolicy.screenshotMode || DEFAULT_TESTING_ENGINE_V2.artifactPolicy.screenshotMode).trim() || DEFAULT_TESTING_ENGINE_V2.artifactPolicy.screenshotMode,
      videoMode: String(artifactPolicy.videoMode || DEFAULT_TESTING_ENGINE_V2.artifactPolicy.videoMode).trim() || DEFAULT_TESTING_ENGINE_V2.artifactPolicy.videoMode
    },
    runtimeBacked: {
      shell: {
        enabled: runtimeBacked?.shell?.enabled === true,
        maxCases: Math.floor(normalizeFiniteNumber(
          runtimeBacked?.shell?.maxCases,
          DEFAULT_TESTING_ENGINE_V2.runtimeBacked.shell.maxCases,
          { min: 1, max: 500 }
        )),
        maxWallClockSharePct: Math.floor(normalizeFiniteNumber(
          runtimeBacked?.shell?.maxWallClockSharePct,
          DEFAULT_TESTING_ENGINE_V2.runtimeBacked.shell.maxWallClockSharePct,
          { min: 0, max: 100 }
        ))
      },
      pluginAction: {
        enabled: runtimeBacked?.pluginAction?.enabled === true,
        criticalOnly: runtimeBacked?.pluginAction?.criticalOnly !== false,
        maxCases: Math.floor(normalizeFiniteNumber(
          runtimeBacked?.pluginAction?.maxCases,
          DEFAULT_TESTING_ENGINE_V2.runtimeBacked.pluginAction.maxCases,
          { min: 1, max: 500 }
        )),
        maxWallClockSharePct: Math.floor(normalizeFiniteNumber(
          runtimeBacked?.pluginAction?.maxWallClockSharePct,
          DEFAULT_TESTING_ENGINE_V2.runtimeBacked.pluginAction.maxWallClockSharePct,
          { min: 0, max: 100 }
        ))
      },
      workflow: {
        enabled: runtimeBacked?.workflow?.enabled === true,
        criticalOnly: runtimeBacked?.workflow?.criticalOnly !== false,
        maxCases: Math.floor(normalizeFiniteNumber(
          runtimeBacked?.workflow?.maxCases,
          DEFAULT_TESTING_ENGINE_V2.runtimeBacked.workflow.maxCases,
          { min: 1, max: 500 }
        )),
        maxWallClockSharePct: Math.floor(normalizeFiniteNumber(
          runtimeBacked?.workflow?.maxWallClockSharePct,
          DEFAULT_TESTING_ENGINE_V2.runtimeBacked.workflow.maxWallClockSharePct,
          { min: 0, max: 100 }
        ))
      },
      uiConsistency: {
        enabled: runtimeBacked?.uiConsistency?.enabled === true,
        maxCases: Math.floor(normalizeFiniteNumber(
          runtimeBacked?.uiConsistency?.maxCases,
          DEFAULT_TESTING_ENGINE_V2.runtimeBacked.uiConsistency.maxCases,
          { min: 1, max: 500 }
        )),
        maxWallClockSharePct: Math.floor(normalizeFiniteNumber(
          runtimeBacked?.uiConsistency?.maxWallClockSharePct,
          DEFAULT_TESTING_ENGINE_V2.runtimeBacked.uiConsistency.maxWallClockSharePct,
          { min: 0, max: 100 }
        ))
      }
    },
    migrationPolicy: {
      primaryPromotionGate: migrationPolicy.primaryPromotionGate === true,
      defaultForNewApps: migrationPolicy.defaultForNewApps !== false,
      promotionWindowDays: Math.floor(normalizeFiniteNumber(
        migrationPolicy.promotionWindowDays,
        DEFAULT_TESTING_ENGINE_V2.migrationPolicy.promotionWindowDays,
        { min: 1, max: 365 }
      )),
      sunsetWindow: String(migrationPolicy.sunsetWindow || DEFAULT_TESTING_ENGINE_V2.migrationPolicy.sunsetWindow).trim() || DEFAULT_TESTING_ENGINE_V2.migrationPolicy.sunsetWindow
    }
  };
}

function normalizeTestingEngine(value = {}) {
  const source = value && typeof value === 'object' ? value : {};
  return {
    ...source,
    enabled: source.enabled !== false,
    enforcementMode: String(source.enforcementMode || 'mandatory').trim() || 'mandatory',
    language: ['js', 'ts', 'mixed'].includes(String(source.language || '').trim().toLowerCase())
      ? String(source.language || '').trim().toLowerCase()
      : 'js',
    runner: ['vitest', 'jest', 'node:test'].includes(String(source.runner || '').trim())
      ? String(source.runner || '').trim()
      : 'node:test',
    generationMode: String(source.generationMode || 'hybrid').trim() || 'hybrid',
    dataMode: String(source.dataMode || 'synthetic-deterministic-factory').trim() || 'synthetic-deterministic-factory',
    obsoletePolicy: String(source.obsoletePolicy || 'quarantine-then-purge').trim() || 'quarantine-then-purge',
    gatePolicy: String(source.gatePolicy || 'warn-only').trim() || 'warn-only',
    networkPolicy: String(source.networkPolicy || 'deny').trim() || 'deny',
    budgets: normalizeTestingEngineBudgets(source.budgets),
    golden: normalizeTestingEngineGolden(source.golden),
    business: normalizeTestingEngineBusiness(source.business),
    v2: normalizeTestingEngineV2(source.v2)
  };
}

function validateRequiredString(errors, value, fieldPath) {
  if (!String(value || '').trim()) {
    errors.push(`Invalid app manifest field: ${fieldPath} is required.`);
  }
}

function validateEnumValue(errors, value, allowedValues, fieldPath) {
  const clean = String(value || '').trim();
  if (clean && !allowedValues.includes(clean)) {
    errors.push(`Invalid app manifest field: ${fieldPath} must be one of ${allowedValues.join('|')}`);
  }
}

function validateStringArray(errors, value, fieldPath, { required = false, minItems = 0 } = {}) {
  if (value === undefined) {
    if (required) errors.push(`Invalid app manifest field: ${fieldPath} must be an array.`);
    return;
  }
  if (!Array.isArray(value)) {
    errors.push(`Invalid app manifest field: ${fieldPath} must be an array.`);
    return;
  }
  const normalized = normalizeStringList(value);
  if (minItems > 0 && normalized.length < minItems) {
    errors.push(`Invalid app manifest field: ${fieldPath} must contain at least ${minItems} item(s).`);
  }
}

function validateObjectArray(errors, value, fieldPath, validateItem) {
  if (value === undefined) return;
  if (!Array.isArray(value)) {
    errors.push(`Invalid app manifest field: ${fieldPath} must be an array.`);
    return;
  }
  value.forEach((item, index) => {
    const itemPath = `${fieldPath}[${index}]`;
    if (!isPlainObject(item)) {
      errors.push(`Invalid app manifest field: ${itemPath} must be an object.`);
      return;
    }
    validateItem(item, itemPath);
  });
}

function validateManifestDataGovernance(source, errors) {
  const rawDataPlane = source.dataPlane;
  if (rawDataPlane !== undefined) {
    if (!isPlainObject(rawDataPlane)) {
      errors.push('Invalid app manifest field: dataPlane must be an object.');
    } else {
      validateEnumValue(errors, rawDataPlane.provider, APP_DATA_PLANE_PROVIDERS, 'dataPlane.provider');
      validateObjectArray(errors, rawDataPlane.bindings, 'dataPlane.bindings', (binding, fieldPath) => {
        validateRequiredString(errors, binding.binding, `${fieldPath}.binding`);
        validateRequiredString(errors, binding.role, `${fieldPath}.role`);
        validateEnumValue(errors, binding.role, APP_DATA_PLANE_BINDING_ROLES, `${fieldPath}.role`);
        validateEnumValue(errors, binding.client, APP_DATA_PLANE_CLIENTS, `${fieldPath}.client`);
      });
      if (rawDataPlane.freshness !== undefined && !isPlainObject(rawDataPlane.freshness)) {
        errors.push('Invalid app manifest field: dataPlane.freshness must be an object.');
      }
    }
  }

  validateObjectArray(errors, source.surfaces, 'surfaces', (surface, fieldPath) => {
    validateRequiredString(errors, surface.surfaceId, `${fieldPath}.surfaceId`);
    validateEnumValue(errors, surface.revisionScope, APP_SURFACE_REVISION_SCOPES, `${fieldPath}.revisionScope`);
    validateEnumValue(errors, surface.revisionSource, APP_SURFACE_REVISION_SOURCES, `${fieldPath}.revisionSource`);
    validateStringArray(errors, surface.readRouteRefs, `${fieldPath}.readRouteRefs`);
    validateStringArray(errors, surface.readModelRefs, `${fieldPath}.readModelRefs`);
  });

  validateObjectArray(errors, source.readRoutes, 'readRoutes', (route, fieldPath) => {
    validateRequiredString(errors, route.routeId, `${fieldPath}.routeId`);
    validateRequiredString(errors, route.path, `${fieldPath}.path`);
    validateRequiredString(errors, route.method, `${fieldPath}.method`);
    validateEnumValue(errors, String(route.method || '').trim().toUpperCase(), APP_READ_ROUTE_METHODS, `${fieldPath}.method`);
    validateStringArray(errors, route.surfaceRefs, `${fieldPath}.surfaceRefs`, { required: true, minItems: 1 });
    validateEnumValue(errors, route.bindingRole, APP_READ_ROUTE_BINDING_ROLES, `${fieldPath}.bindingRole`);
    validateStringArray(errors, route.performanceWorkloadRefs, `${fieldPath}.performanceWorkloadRefs`);
  });

  validateObjectArray(errors, source.mutationSpecs, 'mutationSpecs', (mutation, fieldPath) => {
    validateRequiredString(errors, mutation.mutationId, `${fieldPath}.mutationId`);
    validateRequiredString(errors, mutation.route, `${fieldPath}.route`);
    validateRequiredString(errors, mutation.method, `${fieldPath}.method`);
    validateEnumValue(errors, String(mutation.method || '').trim().toUpperCase(), APP_MUTATION_METHODS, `${fieldPath}.method`);
    validateStringArray(errors, mutation.affectedSurfaces, `${fieldPath}.affectedSurfaces`, { required: true, minItems: 1 });
    validateStringArray(errors, mutation.beforeKeys, `${fieldPath}.beforeKeys`);
    validateStringArray(errors, mutation.afterKeys, `${fieldPath}.afterKeys`);
    validateEnumValue(errors, mutation.refreshScope, APP_REFRESH_SCOPES, `${fieldPath}.refreshScope`);
    if (mutation.responseDelta !== undefined && !isPlainObject(mutation.responseDelta)) {
      errors.push(`Invalid app manifest field: ${fieldPath}.responseDelta must be an object.`);
    } else if (isPlainObject(mutation.responseDelta)) {
      validateStringArray(errors, mutation.responseDelta.fields, `${fieldPath}.responseDelta.fields`);
    }
    validateStringArray(errors, mutation.readModelRefs, `${fieldPath}.readModelRefs`);
    validateRequiredString(errors, mutation.refreshPolicy, `${fieldPath}.refreshPolicy`);
    validateEnumValue(errors, mutation.refreshPolicy, APP_REFRESH_POLICIES, `${fieldPath}.refreshPolicy`);
  });

  validateObjectArray(errors, source.readModels, 'readModels', (model, fieldPath) => {
    validateRequiredString(errors, model.modelId, `${fieldPath}.modelId`);
    validateStringArray(errors, model.surfaceRefs, `${fieldPath}.surfaceRefs`, { required: true, minItems: 1 });
    validateStringArray(errors, model.sourceTables, `${fieldPath}.sourceTables`);
    validateRequiredString(errors, model.refreshPolicy, `${fieldPath}.refreshPolicy`);
    validateEnumValue(errors, model.refreshPolicy, APP_REFRESH_POLICIES, `${fieldPath}.refreshPolicy`);
    if (model.stalenessBudgetSeconds !== undefined && (!Number.isFinite(Number(model.stalenessBudgetSeconds)) || Number(model.stalenessBudgetSeconds) < 0)) {
      errors.push(`Invalid app manifest field: ${fieldPath}.stalenessBudgetSeconds must be a non-negative number.`);
    }
  });

  const rawPerformance = source.performance;
  if (rawPerformance !== undefined) {
    if (!isPlainObject(rawPerformance)) {
      errors.push('Invalid app manifest field: performance must be an object.');
    } else {
      validateObjectArray(errors, rawPerformance.workloads, 'performance.workloads', (workload, fieldPath) => {
        validateRequiredString(errors, workload.workloadId, `${fieldPath}.workloadId`);
        validateRequiredString(errors, workload.category, `${fieldPath}.category`);
        validateEnumValue(errors, workload.category, APP_PERFORMANCE_WORKLOAD_CATEGORIES, `${fieldPath}.category`);
        validateStringArray(errors, workload.surfaceRefs, `${fieldPath}.surfaceRefs`);
        validateStringArray(errors, workload.routeRefs, `${fieldPath}.routeRefs`);
        validateStringArray(errors, workload.mutationRefs, `${fieldPath}.mutationRefs`);
        if (!isPlainObject(workload.budgets)) {
          errors.push(`Invalid app manifest field: ${fieldPath}.budgets must be an object.`);
        }
      });
    }
  }
}

export function resolveAppPluginIds(appManifest = {}) {
  const baselines = normalizePluginIdList(appManifest && appManifest.baselines);
  const plugins = normalizePluginIdList(appManifest && appManifest.plugins);
  return normalizePluginIdList([...baselines, ...plugins]);
}

export function resolveVisibleAppPluginIds(appManifest = {}) {
  const hidden = new Set(HIDDEN_NAV_PLUGIN_IDS);
  return resolveAppPluginIds(appManifest).filter((pluginId) => !hidden.has(pluginId));
}

export function validatePluginManifest(manifest = {}) {
  const source = manifest && typeof manifest === 'object' ? manifest : {};
  const errors = [];
  const rawHelp = source.help && typeof source.help === 'object' && !Array.isArray(source.help)
    ? source.help
    : null;
  const normalized = {
    id: String(source.id || '').trim(),
    name: String(source.name || '').trim(),
    version: String(source.version || '').trim(),
    apiVersion: String(source.apiVersion || '').trim(),
    entry: String(source.entry || '').trim(),
    tab: source.tab && typeof source.tab === 'object' ? source.tab : {},
    nav: source.nav && typeof source.nav === 'object' ? source.nav : {},
    requiredCapabilities: Array.isArray(source.requiredCapabilities)
      ? source.requiredCapabilities.map((item) => String(item || '').trim()).filter(Boolean)
      : [],
    publishedCapabilities: Array.isArray(source.publishedCapabilities)
      ? source.publishedCapabilities.map((item) => String(item || '').trim()).filter(Boolean)
      : [],
    featureFlags: Array.isArray(source.featureFlags)
      ? source.featureFlags.map((item) => String(item || '').trim()).filter(Boolean)
      : [],
    help: normalizePluginHelp(rawHelp)
  };

  if (!normalized.id) errors.push('Missing plugin manifest field: id');
  if (!normalized.name) errors.push('Missing plugin manifest field: name');
  if (!normalized.version) errors.push('Missing plugin manifest field: version');
  if (!normalized.apiVersion) errors.push('Missing plugin manifest field: apiVersion');
  if (!normalized.entry) errors.push('Missing plugin manifest field: entry');

  const tabLabel = String(normalized.tab.label || '').trim();
  const tabOrder = Number(normalized.tab.order);
  if (!tabLabel) errors.push('Missing plugin manifest field: tab.label');
  if (!Number.isFinite(tabOrder)) errors.push('Invalid plugin manifest field: tab.order must be numeric');

  if (normalized.apiVersion && !isPluginApiCompatible(normalized.apiVersion)) {
    errors.push(
      `Plugin apiVersion ${normalized.apiVersion} is incompatible with platform ${PLATFORM_PLUGIN_API_VERSION}`
    );
  }
  if (source.help !== undefined && !rawHelp) {
    errors.push('Invalid plugin manifest field: help must be an object.');
  }
  if (rawHelp && rawHelp.audience !== undefined && !Array.isArray(rawHelp.audience)) {
    errors.push('Invalid plugin manifest field: help.audience must be an array.');
  }
  if (rawHelp && rawHelp.userFlows !== undefined && !Array.isArray(rawHelp.userFlows)) {
    errors.push('Invalid plugin manifest field: help.userFlows must be an array.');
  }
  if (rawHelp && rawHelp.docRefs !== undefined && !Array.isArray(rawHelp.docRefs)) {
    errors.push('Invalid plugin manifest field: help.docRefs must be an array.');
  }
  for (const flow of normalized.help?.userFlows || []) {
    if (!flow.flowId) errors.push('Invalid plugin manifest field: help.userFlows[].flowId is required.');
    if (!flow.title) errors.push(`Invalid plugin manifest field: help.userFlows[${flow.flowId || '?'}].title is required.`);
  }

  return {
    ok: errors.length === 0,
    errors,
    manifest: {
      ...normalized,
      nav: {
        hidden: Boolean(normalized.nav.hidden)
      },
      tab: {
        label: tabLabel,
        order: Number.isFinite(tabOrder) ? tabOrder : 0
      },
      ...(normalized.help ? { help: normalized.help } : {})
    }
  };
}

export function validateAppManifest(appManifest = {}) {
  const source = appManifest && typeof appManifest === 'object' ? appManifest : {};
  const errors = [];
  const rawPlatformCore = source && typeof source.platformCore === 'object' && source.platformCore
    ? source.platformCore
    : {};
  const rawPlatformCoreMode = String(rawPlatformCore.mode || PLATFORM_CORE_DEFAULT_MODE).trim().toLowerCase();
  const normalizedPlatformCoreMode = rawPlatformCoreMode === 'channel' ? 'channel' : PLATFORM_CORE_DEFAULT_MODE;
  const normalizedPlatformCore = {
    mode: normalizedPlatformCoreMode,
    channel: String(rawPlatformCore.channel || '').trim(),
    version: String(rawPlatformCore.version || '').trim(),
    minVersion: String(rawPlatformCore.minVersion || '').trim(),
    manifestUrl: String(rawPlatformCore.manifestUrl || '').trim(),
    rollbackVersion: String(rawPlatformCore.rollbackVersion || '').trim()
  };
  const rawUiTheme = source && typeof source.uiTheme === 'object' && source.uiTheme
    ? source.uiTheme
    : {};
  const rawOperations = source && typeof source.operations === 'object' && source.operations && !Array.isArray(source.operations)
    ? source.operations
    : null;
  const rawDataPlane = isPlainObject(source.dataPlane) ? source.dataPlane : null;
  const rawPerformance = isPlainObject(source.performance) ? source.performance : null;
  const rawHelp = source && typeof source.help === 'object' && source.help && !Array.isArray(source.help)
    ? source.help
    : null;
  const hasNewThemeFields = Object.prototype.hasOwnProperty.call(rawUiTheme, 'appearanceId')
    || Object.prototype.hasOwnProperty.call(rawUiTheme, 'packId');
  const hasLegacyThemeField = Object.prototype.hasOwnProperty.call(rawUiTheme, 'defaultPackId');

  const rawAppearanceId = String(rawUiTheme.appearanceId || '').trim().toLowerCase();
  const rawPackId = String(rawUiTheme.packId || '').trim().toLowerCase();
  const rawDefaultPackId = String(rawUiTheme.defaultPackId || APP_THEME_DEFAULT_PACK_ID).trim().toLowerCase();
  const rawAppScope = String(rawUiTheme.appScope || source.appId || '').trim();

  let normalizedAppearanceId = APP_THEME_DEFAULT_APPEARANCE_ID;
  let normalizedPackId = APP_THEME_DEFAULT_APPEARANCE_PACK_ID;
  if (hasNewThemeFields) {
    normalizedAppearanceId = APP_THEME_APPEARANCE_IDS.includes(rawAppearanceId)
      ? rawAppearanceId
      : APP_THEME_DEFAULT_APPEARANCE_ID;
    const allowedPacks = APP_THEME_PACKS_BY_APPEARANCE[normalizedAppearanceId] || [APP_THEME_DEFAULT_APPEARANCE_PACK_ID];
    normalizedPackId = allowedPacks.includes(rawPackId)
      ? rawPackId
      : (allowedPacks[0] || APP_THEME_DEFAULT_APPEARANCE_PACK_ID);
  } else {
    if (rawDefaultPackId === 'executive' || rawDefaultPackId === 'compact') {
      normalizedAppearanceId = 'figma';
      normalizedPackId = rawDefaultPackId;
    } else {
      normalizedAppearanceId = 'classic';
      normalizedPackId = 'default';
    }
  }

  const normalized = {
    appId: String(source.appId || '').trim(),
    appName: String(source.appName || '').trim(),
    runtime: String(source.runtime || 'manifest-platform').trim(),
    defaultTab: String(source.defaultTab || '').trim(),
    defaultScreenId: String(source.defaultScreenId || '').trim(),
    authPolicy: String(source.authPolicy || 'required').trim(),
    apiBaseUrl: String(source.apiBaseUrl || '').trim(),
    platformCore: normalizedPlatformCore,
    uiTheme: {
      appearanceId: normalizedAppearanceId,
      packId: normalizedPackId,
      defaultPackId: normalizedPackId,
      appScope: rawAppScope
    },
    shellPreset: APP_SHELL_PRESET_VALUES.includes(String(source.shellPreset || '').trim().toLowerCase())
      ? String(source.shellPreset || '').trim().toLowerCase()
      : APP_SHELL_PRESET_DEFAULT,
    authBaseline: APP_AUTH_BASELINE_VALUES.includes(String(source.authBaseline || '').trim().toLowerCase())
      ? String(source.authBaseline || '').trim().toLowerCase()
      : APP_AUTH_BASELINE_DEFAULT,
    shell: {
      version: String(source && source.shell && source.shell.version || '').trim() || 'v1',
      layout: APP_SHELL_LAYOUT_VALUES.includes(String(source && source.shell && source.shell.layout || '').trim().toLowerCase())
        ? String(source.shell.layout || '').trim().toLowerCase()
        : APP_SHELL_LAYOUT_DEFAULT,
      navMode: APP_SHELL_NAV_MODE_VALUES.includes(String(source && source.shell && source.shell.navMode || '').trim().toLowerCase())
        ? String(source.shell.navMode || '').trim().toLowerCase()
        : APP_SHELL_NAV_MODE_DEFAULT,
      mobileNavMode: APP_SHELL_MOBILE_NAV_MODE_VALUES.includes(String(source && source.shell && source.shell.mobileNavMode || '').trim().toLowerCase())
        ? String(source.shell.mobileNavMode || '').trim().toLowerCase()
        : APP_SHELL_MOBILE_NAV_MODE_DEFAULT
    },
    baselines: normalizePluginIdList(source.baselines),
    plugins: normalizePluginIdList(source.plugins),
    resourceIsolation: String(source.resourceIsolation || APP_RESOURCE_ISOLATION_DEFAULT).trim(),
    dependencyProfile: String(source.dependencyProfile || APP_DEPENDENCY_PROFILE_DEFAULT).trim(),
    dependencyExtensions: normalizeStringList(source.dependencyExtensions),
    previewAdapterId: String(source.previewAdapterId || APP_PREVIEW_ADAPTER_DEFAULT).trim() || APP_PREVIEW_ADAPTER_DEFAULT,
    previewModes: normalizePreviewModeList(source.previewModes),
    previewDataProfile: String(source.previewDataProfile || APP_PREVIEW_DATA_PROFILE_DEFAULT).trim() || APP_PREVIEW_DATA_PROFILE_DEFAULT,
    operations: rawOperations
      ? {
          ownerTeam: String(rawOperations.ownerTeam || '').trim(),
          serviceTier: String(rawOperations.serviceTier || '').trim().toLowerCase(),
          reliabilityProfileId: String(rawOperations.reliabilityProfileId || '').trim(),
          observabilityProfileId: String(rawOperations.observabilityProfileId || '').trim(),
          deployPolicyId: String(rawOperations.deployPolicyId || '').trim(),
          runbookRefs: normalizeStringList(rawOperations.runbookRefs)
        }
      : undefined,
    dataPlane: rawDataPlane ? normalizeDataPlane(rawDataPlane) : undefined,
    surfaces: source.surfaces !== undefined ? normalizeManifestSurfaces(source.surfaces) : undefined,
    readRoutes: source.readRoutes !== undefined ? normalizeManifestReadRoutes(source.readRoutes) : undefined,
    mutationSpecs: source.mutationSpecs !== undefined ? normalizeManifestMutationSpecs(source.mutationSpecs) : undefined,
    readModels: source.readModels !== undefined ? normalizeManifestReadModels(source.readModels) : undefined,
    performance: rawPerformance ? normalizeManifestPerformance(rawPerformance) : undefined,
    help: normalizeAppHelp(rawHelp),
    settingsPlugin: normalizeSettingsPluginPolicy(source.settingsPlugin, errors),
    testingEngine: source.testingEngine && typeof source.testingEngine === 'object'
      ? normalizeTestingEngine(source.testingEngine)
      : undefined
  };

  if (!normalized.appId) errors.push('Missing app manifest field: appId');
  if (!normalized.appName) errors.push('Missing app manifest field: appName');
  if (normalized.runtime !== 'manifest-platform' && normalized.runtime !== 'legacy-dashboard') {
    errors.push('Invalid app manifest field: runtime must be manifest-platform or legacy-dashboard');
  }
  if (normalized.authPolicy !== 'required' && normalized.authPolicy !== 'optional') {
    errors.push('Invalid app manifest field: authPolicy must be required or optional');
  }
  if (!APP_AUTH_BASELINE_VALUES.includes(normalized.authBaseline)) {
    errors.push(`Invalid app manifest field: authBaseline must be one of ${APP_AUTH_BASELINE_VALUES.join('|')}`);
  }
  if (!APP_SHELL_PRESET_VALUES.includes(normalized.shellPreset)) {
    errors.push(`Invalid app manifest field: shellPreset must be one of ${APP_SHELL_PRESET_VALUES.join('|')}`);
  }
  if (!String(normalized.shell.version || '').trim()) {
    errors.push('Invalid app manifest field: shell.version must be non-empty');
  }
  if (!APP_SHELL_LAYOUT_VALUES.includes(normalized.shell.layout)) {
    errors.push(`Invalid app manifest field: shell.layout must be one of ${APP_SHELL_LAYOUT_VALUES.join('|')}`);
  }
  if (!APP_SHELL_NAV_MODE_VALUES.includes(normalized.shell.navMode)) {
    errors.push(`Invalid app manifest field: shell.navMode must be one of ${APP_SHELL_NAV_MODE_VALUES.join('|')}`);
  }
  if (!APP_SHELL_MOBILE_NAV_MODE_VALUES.includes(normalized.shell.mobileNavMode)) {
    errors.push(`Invalid app manifest field: shell.mobileNavMode must be one of ${APP_SHELL_MOBILE_NAV_MODE_VALUES.join('|')}`);
  }
  if (!APP_RESOURCE_ISOLATION_VALUES.includes(normalized.resourceIsolation)) {
    errors.push(`Invalid app manifest field: resourceIsolation must be one of ${APP_RESOURCE_ISOLATION_VALUES.join('|')}`);
  }
  if (!normalized.dependencyProfile) {
    errors.push('Invalid app manifest field: dependencyProfile must be non-empty');
  } else if (!APP_DEPENDENCY_PROFILE_APPROVED.includes(normalized.dependencyProfile)) {
    errors.push(`Invalid app manifest field: dependencyProfile must be one of ${APP_DEPENDENCY_PROFILE_APPROVED.join('|')}`);
  }
  if (source.dependencyExtensions !== undefined && !Array.isArray(source.dependencyExtensions)) {
    errors.push('Invalid app manifest field: dependencyExtensions must be an array.');
  }
  if (!String(normalized.previewAdapterId || '').trim()) {
    errors.push('Invalid app manifest field: previewAdapterId must be non-empty');
  }
  if (source.previewModes !== undefined && !Array.isArray(source.previewModes)) {
    errors.push('Invalid app manifest field: previewModes must be an array.');
  }
  if (!Array.isArray(normalized.previewModes) || normalized.previewModes.length === 0) {
    errors.push('Invalid app manifest field: previewModes must contain at least one supported preview mode.');
  } else if (normalized.previewModes.some((item) => !APP_PREVIEW_MODE_VALUES.includes(item))) {
    errors.push(`Invalid app manifest field: previewModes must only include ${APP_PREVIEW_MODE_VALUES.join('|')}`);
  }
  if (!String(normalized.previewDataProfile || '').trim()) {
    errors.push('Invalid app manifest field: previewDataProfile must be non-empty');
  }
  if (source.operations !== undefined && (!rawOperations || Array.isArray(source.operations))) {
    errors.push('Invalid app manifest field: operations must be an object.');
  }
  if (source.help !== undefined && !rawHelp) {
    errors.push('Invalid app manifest field: help must be an object.');
  }
  if (rawHelp && rawHelp.audience !== undefined && !Array.isArray(rawHelp.audience)) {
    errors.push('Invalid app manifest field: help.audience must be an array.');
  }
  if (rawHelp && rawHelp.majorFlows !== undefined && !Array.isArray(rawHelp.majorFlows)) {
    errors.push('Invalid app manifest field: help.majorFlows must be an array.');
  }
  if (rawHelp && rawHelp.docRefs !== undefined && !Array.isArray(rawHelp.docRefs)) {
    errors.push('Invalid app manifest field: help.docRefs must be an array.');
  }
  for (const flow of normalized.help?.majorFlows || []) {
    if (!flow.flowId) errors.push('Invalid app manifest field: help.majorFlows[].flowId is required.');
    if (!flow.title) errors.push(`Invalid app manifest field: help.majorFlows[${flow.flowId || '?'}].title is required.`);
  }
  if (
    rawOperations
    && normalized.operations?.serviceTier
    && !APP_SERVICE_TIER_VALUES.includes(normalized.operations.serviceTier)
  ) {
    errors.push(`Invalid app manifest field: operations.serviceTier must be one of ${APP_SERVICE_TIER_VALUES.join('|')}`);
  }
  if (rawOperations && rawOperations.runbookRefs !== undefined && !Array.isArray(rawOperations.runbookRefs)) {
    errors.push('Invalid app manifest field: operations.runbookRefs must be an array.');
  }
  validateManifestDataGovernance(source, errors);
  if (rawPlatformCore.mode && !PLATFORM_CORE_MODES.includes(rawPlatformCoreMode)) {
    errors.push(`Invalid app manifest field: platformCore.mode must be one of ${PLATFORM_CORE_MODES.join('|')}`);
  }
  if (normalized.platformCore.mode === 'channel') {
    if (!normalized.platformCore.channel) {
      errors.push('Invalid app manifest field: platformCore.channel is required when mode=channel');
    }
    if (!normalized.platformCore.version) {
      errors.push('Invalid app manifest field: platformCore.version is required when mode=channel');
    }
    if (!normalized.platformCore.minVersion) {
      errors.push('Invalid app manifest field: platformCore.minVersion is required when mode=channel');
    }
    if (!normalized.platformCore.manifestUrl) {
      errors.push('Invalid app manifest field: platformCore.manifestUrl is required when mode=channel');
    }
    if (
      normalized.platformCore.version
      && PLATFORM_CORE_FORBIDDEN_FLOATING_VERSIONS.includes(normalized.platformCore.version.toLowerCase())
    ) {
      errors.push(`Invalid app manifest field: platformCore.version cannot be floating (${PLATFORM_CORE_FORBIDDEN_FLOATING_VERSIONS.join('|')})`);
    }
    if (normalized.platformCore.version && !isSemverLike(normalized.platformCore.version)) {
      errors.push('Invalid app manifest field: platformCore.version must be SemVer (x.y.z).');
    }
    if (normalized.platformCore.minVersion && !isSemverLike(normalized.platformCore.minVersion)) {
      errors.push('Invalid app manifest field: platformCore.minVersion must be SemVer (x.y.z).');
    }
    if (
      normalized.platformCore.version
      && normalized.platformCore.minVersion
      && isSemverLike(normalized.platformCore.version)
      && isSemverLike(normalized.platformCore.minVersion)
      && compareSemver(normalized.platformCore.version, normalized.platformCore.minVersion) < 0
    ) {
      errors.push('Invalid app manifest field: platformCore.version must be >= platformCore.minVersion.');
    }
  } else if (normalized.platformCore.minVersion && !isSemverLike(normalized.platformCore.minVersion)) {
    errors.push('Invalid app manifest field: platformCore.minVersion must be SemVer (x.y.z).');
  }
  if (hasLegacyThemeField && !APP_THEME_PACK_IDS.includes(rawDefaultPackId)) {
    errors.push(`Invalid app manifest field: uiTheme.defaultPackId must be one of ${APP_THEME_PACK_IDS.join('|')}`);
  }
  if (hasNewThemeFields) {
    if (!APP_THEME_APPEARANCE_IDS.includes(rawAppearanceId)) {
      errors.push(`Invalid app manifest field: uiTheme.appearanceId must be one of ${APP_THEME_APPEARANCE_IDS.join('|')}`);
    }
    const allowedPacks = APP_THEME_PACKS_BY_APPEARANCE[rawAppearanceId] || [];
    if (!allowedPacks.includes(rawPackId)) {
      errors.push(`Invalid app manifest field: uiTheme.packId must be one of ${allowedPacks.join('|') || 'none'} for appearance ${rawAppearanceId || '(empty)'}`);
    }
  }
  if (!normalized.uiTheme.appScope) {
    errors.push('Invalid app manifest field: uiTheme.appScope must be non-empty');
  }
  const settingsContributionProviderId = String(
    normalized.settingsPlugin?.appContribution?.providerPluginId || ''
  ).trim();
  if (
    settingsContributionProviderId
    && !resolveAppPluginIds(normalized).includes(settingsContributionProviderId)
  ) {
    errors.push(
      `Invalid app manifest field: settingsPlugin.appContribution.providerPluginId '${settingsContributionProviderId}' must be declared in baselines or plugins.`
    );
  }
  if (source.testingEngine !== undefined && (!source.testingEngine || typeof source.testingEngine !== 'object' || Array.isArray(source.testingEngine))) {
    errors.push('Invalid app manifest field: testingEngine must be an object.');
  }
  if (source?.testingEngine?.business && typeof source.testingEngine.business === 'object' && !Array.isArray(source.testingEngine.business)) {
    const rawPolicy = String(source.testingEngine.business.structuralFallbackPolicy || '').trim().toLowerCase();
    if (
      rawPolicy
      && !TESTING_ENGINE_BUSINESS_FALLBACK_POLICIES.includes(rawPolicy)
    ) {
      errors.push(`Invalid app manifest field: testingEngine.business.structuralFallbackPolicy must be one of ${TESTING_ENGINE_BUSINESS_FALLBACK_POLICIES.join('|')}`);
    }
    const rawRatio = source.testingEngine.business.minBusinessCoverageRatio;
    if (rawRatio !== undefined && (!Number.isFinite(Number(rawRatio)) || Number(rawRatio) < 0 || Number(rawRatio) > 1)) {
      errors.push('Invalid app manifest field: testingEngine.business.minBusinessCoverageRatio must be between 0 and 1.');
    }
  }
  if (source?.testingEngine?.v2 !== undefined && (!source.testingEngine.v2 || typeof source.testingEngine.v2 !== 'object' || Array.isArray(source.testingEngine.v2))) {
    errors.push('Invalid app manifest field: testingEngine.v2 must be an object.');
  }
  if (source?.testingEngine?.v2 && typeof source.testingEngine.v2 === 'object' && !Array.isArray(source.testingEngine.v2)) {
    const rawExecutionMode = String(source.testingEngine.v2.executionMode || '').trim().toLowerCase();
    if (rawExecutionMode && !TESTING_ENGINE_V2_EXECUTION_MODES.includes(rawExecutionMode)) {
      errors.push(`Invalid app manifest field: testingEngine.v2.executionMode must be one of ${TESTING_ENGINE_V2_EXECUTION_MODES.join('|')}`);
    }
    const requiredChecks = source.testingEngine.v2?.ciPolicy?.requiredChecks;
    if (requiredChecks !== undefined && !Array.isArray(requiredChecks)) {
      errors.push('Invalid app manifest field: testingEngine.v2.ciPolicy.requiredChecks must be an array.');
    }
  }

  const declaredPluginIds = resolveAppPluginIds(normalized);
  const visiblePluginIds = resolveVisibleAppPluginIds(normalized);
  const isPlatformStudioShell = normalized.shellPreset === 'sectioned-routes';

  const allowTotallyEmptyShell = isPlatformStudioShell
    || (normalized.shellPreset === 'empty' && normalized.authBaseline === 'without-auth-login');
  if (normalized.runtime === 'manifest-platform' && declaredPluginIds.length === 0 && !allowTotallyEmptyShell) {
    errors.push('Manifest-platform apps must include at least one plugin in app manifest baselines[] or plugins[] (or explicitly choose empty shell without auth baseline).');
  }
  if (normalized.runtime === 'manifest-platform') {
    if (isPlatformStudioShell) {
      if (normalized.shell.layout !== 'sidebar-routes') {
        errors.push('Invalid app manifest field: shell.layout must be sidebar-routes when shellPreset=sectioned-routes.');
      }
      if (normalized.shell.navMode !== 'sectioned-routes') {
        errors.push('Invalid app manifest field: shell.navMode must be sectioned-routes when shellPreset=sectioned-routes.');
      }
      if (!normalized.defaultScreenId) {
        errors.push('Missing app manifest field: defaultScreenId (required when shellPreset=sectioned-routes).');
      }
      if (normalized.defaultTab) {
        errors.push('Invalid app manifest field: defaultTab is not used when shellPreset=sectioned-routes.');
      }
    } else if (!normalized.defaultTab && visiblePluginIds.length > 0) {
      errors.push('Missing app manifest field: defaultTab (required when visible plugin tabs exist).');
    } else if (normalized.defaultTab && !declaredPluginIds.includes(normalized.defaultTab)) {
      errors.push('Invalid app manifest field: defaultTab must be declared in baselines[] or plugins[].');
    } else if (normalized.defaultTab && visiblePluginIds.length > 0 && !visiblePluginIds.includes(normalized.defaultTab)) {
      errors.push('Invalid app manifest field: defaultTab must reference a visible plugin tab.');
    }
  }
  if (normalized.runtime === 'manifest-platform' && normalized.resourceIsolation === 'strict-app') {
    const requiredBaselines = [
      ...(STRICT_BASELINES_BY_SHELL_PRESET[normalized.shellPreset] || APP_BASELINE_DEFAULTS),
      ...(normalized.authBaseline === 'with-auth-login' ? ['platform.auth-login'] : [])
    ];
    const missingRequiredBaselines = requiredBaselines.filter((pluginId) => !normalized.baselines.includes(pluginId));
    if (missingRequiredBaselines.length > 0) {
      errors.push(
        `Strict-app manifests must include required baselines: ${missingRequiredBaselines.join(', ')}`
      );
    }
    if (!APP_DEPENDENCY_PROFILE_APPROVED.includes(normalized.dependencyProfile)) {
      errors.push(
        `Strict-app manifests must use dependencyProfile in ${APP_DEPENDENCY_PROFILE_APPROVED.join('|')}`
      );
    }
  }

  return {
    ok: errors.length === 0,
    errors,
    appManifest: normalized
  };
}

export function resolvePluginLifecycle(rawModule = {}) {
  const direct = rawModule.default && typeof rawModule.default === 'object'
    ? rawModule.default
    : rawModule;

  if (
    direct
    && typeof direct.mount === 'function'
    && typeof direct.refresh === 'function'
    && typeof direct.unmount === 'function'
  ) {
    return {
      mount: (ctx) => direct.mount(ctx),
      refresh: (ctx) => direct.refresh(ctx),
      reactivate: typeof direct.reactivate === 'function' ? (ctx) => direct.reactivate(ctx) : undefined,
      unmount: (ctx) => direct.unmount(ctx),
      health: typeof direct.health === 'function' ? () => direct.health() : undefined
    };
  }

  if (typeof rawModule.init === 'function') {
    return adaptLegacyInitPlugin(rawModule.init);
  }
  if (rawModule.default && typeof rawModule.default.init === 'function') {
    return adaptLegacyInitPlugin(rawModule.default.init);
  }

  throw new Error('Plugin module does not expose lifecycle methods or legacy init(deps).');
}

export function adaptLegacyInitPlugin(initFn) {
  if (typeof initFn !== 'function') {
    throw new Error('Legacy init adapter requires init function');
  }
  let runtime = null;
  let mountedCtx = null;

  return {
    async mount(ctx) {
      mountedCtx = ctx;
      runtime = await initFn(ctx);
    },
    async refresh(ctx) {
      mountedCtx = ctx || mountedCtx;
      if (!runtime || typeof runtime.refresh !== 'function') return;
      await runtime.refresh(mountedCtx);
    },
    async reactivate(ctx) {
      mountedCtx = ctx || mountedCtx;
      if (!runtime || typeof runtime.reactivate !== 'function') return;
      await runtime.reactivate(mountedCtx);
    },
    async unmount(ctx) {
      const nextCtx = ctx || mountedCtx;
      if (runtime && typeof runtime.unmount === 'function') {
        await runtime.unmount(nextCtx);
      }
      runtime = null;
      mountedCtx = null;
    }
  };
}

export function adaptDirectLifecyclePlugin(lifecycleModule) {
  const moduleObj = lifecycleModule && typeof lifecycleModule === 'object' ? lifecycleModule : {};
  if (
    typeof moduleObj.mount !== 'function'
    || typeof moduleObj.refresh !== 'function'
    || typeof moduleObj.unmount !== 'function'
  ) {
    throw new Error('Direct lifecycle adapter requires mount/refresh/unmount functions.');
  }

  return {
    async mount(ctx) {
      const next = { ...ctx, root: ctx.mountEl || ctx.root };
      await moduleObj.mount(next);
    },
    async refresh(ctx) {
      const next = { ...ctx, root: ctx.mountEl || ctx.root };
      await moduleObj.refresh(next);
    },
    async reactivate(ctx) {
      const next = { ...ctx, root: ctx.mountEl || ctx.root };
      if (typeof moduleObj.reactivate !== 'function') return;
      await moduleObj.reactivate(next);
    },
    async unmount(ctx) {
      const next = { ...ctx, root: ctx.mountEl || ctx.root };
      await moduleObj.unmount(next);
    },
    health: typeof moduleObj.health === 'function' ? () => moduleObj.health() : undefined
  };
}
